// src/pages/HowItWorks.tsx
// Redesigned to match the new MySatsang design system.

import React from "react";

import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";

const T = {
  ink:        "#0F0F0F",
  jade:       "#0F6E56",
  jadeMid:    "#1D9E75",
  jadeLight:  "#E1F5EE",
  saffron:    "#D97706",
  saffronMid: "#F59E0B",
  slate:      "#6B7280",
  border:     "rgba(0,0,0,0.08)",
  white:      "#FFFFFF",
};

const STEPS = [
  {
    n: "01",
    emoji: "👤",
    title: "Sign up or sign in",
    desc: "Create a free account with just an email and password. Your data stays private and secure under your account.",
    bg: T.jadeLight,
    accent: T.jade,
  },
  {
    n: "02",
    emoji: "📿",
    title: "Start Gun Grahan",
    desc: "Record virtues and teachings in a structured digital format. Gungrahan Karvathi jiv Brahmrup thay che — capture and grow.",
    bg: "#FEF3C7",
    accent: T.saffron,
  },
  {
    n: "03",
    emoji: "📝",
    title: "Write Pravachan Notes",
    desc: "Rich-text editor with bold, italic, colors, and images. Record discourses live with voice recording, or upload existing audio files.",
    bg: "#F0F4FF",
    accent: "#4F46E5",
  },
  {
    n: "04",
    emoji: "✅",
    title: "Track daily tasks",
    desc: "Keep track of your daily Satsang checklist — Mansi, Vanchan, Antardrasti, and more. Build spiritual discipline one day at a time.",
    bg: "#F0FDF4",
    accent: "#15803D",
  },
  {
    n: "05",
    emoji: "📄",
    title: "Export as PDF",
    desc: "Download clean, formatted PDFs of your Gun Grahan reports and Pravachan notes for sharing, archiving, or printing.",
    bg: "#FFF1F2",
    accent: "#BE185D",
  },
  {
    n: "06",
    emoji: "🕊️",
    title: "Share with the community",
    desc: "Post your pravachans to the public Satsang feed. Fellow devotees can like, comment, and get inspired — or keep everything private.",
    bg: "#FDF4FF",
    accent: "#7E22CE",
  },
];

export default function HowItWorks() {
  const navigate = useNavigate();

  return (
    <div style={{
      minHeight: "100vh",
      background: "linear-gradient(160deg,#FAFAF8 0%,#F0FBF6 50%,#FFFBEB 100%)",
      fontFamily: "'Inter',system-ui,sans-serif",
      padding: "80px 24px 60px",
    }}>
      <div style={{ maxWidth: 760, margin: "0 auto" }}>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          style={{ textAlign: "center", marginBottom: 56 }}
        >
          <p style={{
            fontSize: 11, fontWeight: 700, letterSpacing: "0.12em",
            textTransform: "uppercase", color: T.jadeMid, margin: "0 0 10px",
          }}>
            Getting started
          </p>
          <h1 style={{
            fontFamily: "'Georgia','Times New Roman',serif",
            fontSize: "clamp(28px,5vw,46px)", fontWeight: 700,
            letterSpacing: "-0.02em", color: T.ink, margin: "0 0 14px",
          }}>
            How it works
          </h1>
          <p style={{
            fontSize: 16, color: T.slate, lineHeight: 1.65,
            maxWidth: 480, margin: "0 auto",
          }}>
            MySatsang helps you stay consistent with your spiritual practices.
            Here's everything you can do.
          </p>
        </motion.div>

        {/* Steps */}
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {STEPS.map((step, i) => (
            <motion.div
              key={step.n}
              initial={{ opacity: 0, x: -16 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.45, delay: i * 0.06 }}
              style={{
                display: "flex", gap: 20, alignItems: "flex-start",
                background: step.bg, borderRadius: 16,
                padding: "24px 24px",
                border: "1px solid rgba(0,0,0,0.04)",
              }}
            >
              {/* Step number */}
              <div style={{
                flexShrink: 0, width: 44, height: 44,
                borderRadius: 12,
                background: step.accent,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 13, fontWeight: 700, color: T.white,
                letterSpacing: "0.02em",
              }}>
                {step.n}
              </div>

              <div style={{ flex: 1 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                  <span style={{ fontSize: 18 }}>{step.emoji}</span>
                  <h3 style={{
                    fontSize: 16, fontWeight: 700, color: T.ink,
                    margin: 0, letterSpacing: "-0.01em",
                  }}>
                    {step.title}
                  </h3>
                </div>
                <p style={{ fontSize: 14, color: T.slate, lineHeight: 1.65, margin: 0 }}>
                  {step.desc}
                </p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Privacy note */}
        <motion.p
          initial={{ opacity: 0 }} whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          style={{
            marginTop: 36, textAlign: "center",
            fontSize: 14, color: T.slate, lineHeight: 1.6,
            fontStyle: "italic",
          }}
        >
          All your data is kept private and secure. Use this space to grow
          spiritually, stay disciplined, and connect meaningfully.
        </motion.p>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          style={{
            marginTop: 56,
            background: T.ink,
            borderRadius: 20,
            padding: "48px 40px",
            textAlign: "center",
          }}
        >
          <p style={{
            fontSize: 11, fontWeight: 700, letterSpacing: "0.1em",
            textTransform: "uppercase", color: T.saffronMid, margin: "0 0 10px",
          }}>
            Ready?
          </p>
          <h2 style={{
            fontFamily: "'Georgia','Times New Roman',serif",
            fontSize: 28, fontWeight: 700, color: T.white,
            letterSpacing: "-0.02em", margin: "0 0 12px",
          }}>
            Begin your digital satsang
          </h2>
          <p style={{
            fontSize: 14, color: "rgba(255,255,255,0.55)",
            margin: "0 0 24px", lineHeight: 1.6,
          }}>
            Join others preserving their spiritual journey with these tools.
          </p>
          <button
            onClick={() => navigate("/signup")}
            style={{
              background: T.saffronMid, color: T.white,
              border: "none", borderRadius: 10,
              padding: "13px 28px", fontSize: 15, fontWeight: 600,
              cursor: "pointer", fontFamily: "inherit", letterSpacing: "0.01em",
            }}
          >
            Create free account →
          </button>
        </motion.div>
      </div>
    </div>
  );
}